import { useState, useRef } from 'react';
import { ChevronDown, Pencil as Edit2, Copy, Check, MessageSquare, Link2, Plus, Trash2, Clock, Send, XCircle, ShieldCheck, AlertTriangle, Braces, Info, Eye } from 'lucide-react';
import { parseContentWithVariables } from './VariablePill';
import { AIRegenerateButton } from './AIRegenerateButton';
import { toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import type { WaTemplateApprovalStatus, WaTemplateSubmission } from '../../types/settings';

interface WhatsAppBubblePreviewProps {
  title: string;
  content: string;
  placeholder?: string;
  onUpdate: (content: string) => void;
  onGenerateAI?: (tone: string, length: string, currentContent: string) => Promise<string>;
  maxLength?: number;
  defaultExpanded?: boolean;
  buttonText?: string;
  buttonUrl?: string;
  onUpdateButton?: (buttonText: string, buttonUrl: string) => void;
  approvalStatus?: WaTemplateApprovalStatus;
  pendingSubmission?: WaTemplateSubmission | null;
  onSubmitForApproval?: (text: string, buttonText: string, buttonUrl: string) => Promise<void>;
  submitting?: boolean;
  hasApprovedVersion?: boolean;
  readOnly?: boolean;
}

function StatusBadge({ status }: { status: WaTemplateApprovalStatus }) {
  switch (status) {
    case 'active':
      return (
        <span className="px-2.5 py-0.5 bg-[#05E0D1]/10 text-[#0bb5a5] text-xs font-semibold rounded-full flex items-center gap-1.5 border border-[#05E0D1]/25">
          <ShieldCheck className="w-3 h-3" />
          Active
        </span>
      );
    case 'in_review':
      return (
        <span className="px-2.5 py-0.5 bg-[#1875FF]/10 text-[#1875FF] text-xs font-semibold rounded-full flex items-center gap-1.5 border border-[#1875FF]/25 animate-pulse">
          <Clock className="w-3 h-3" />
          In Review
        </span>
      );
    case 'rejected':
      return (
        <span className="px-2.5 py-0.5 bg-red-50 text-red-700 text-xs font-semibold rounded-full flex items-center gap-1.5 border border-red-200">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    default:
      return (
        <span className="px-2.5 py-0.5 bg-[#05E0D1]/10 text-[#0bb5a5] text-xs font-semibold rounded-full flex items-center gap-1.5 border border-[#05E0D1]/25">
          Draft - Review Pending
          <Eye className="w-3 h-3" />
        </span>
      );
  }
}

function normalizeWhatsAppText(text: string): string {
  return text.replace(/\s*\/\s*/g, '\n');
}

function BubbleRenderer({ text, btnText, btnUrl, label }: { text: string; btnText?: string; btnUrl?: string; label?: string }) {
  const hasBtn = btnText && btnUrl;
  if (!text) return null;

  const normalizedText = normalizeWhatsAppText(text);

  return (
    <div>
      {label && (
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-widest font-sans">{label}</p>
      )}
      <div className="flex justify-end">
        <div className="max-w-sm">
          <div className="bg-green-100 text-gray-900 rounded-2xl rounded-tr-sm shadow-sm overflow-hidden">
            <div className="px-4 py-3">
              <div className="text-sm leading-relaxed whitespace-pre-wrap">
                {parseContentWithVariables(normalizedText).map((part, idx) => {
                  if (part.type === 'variable') {
                    return (
                      <span
                        key={idx}
                        className="inline-flex items-center px-1.5 py-0.5 bg-green-200 text-gray-900 rounded text-xs font-medium mx-0.5"
                      >
                        {part.content}
                      </span>
                    );
                  }
                  return <span key={idx}>{part.content}</span>;
                })}
              </div>
            </div>
            {hasBtn && (
              <div className="border-t border-green-200/60">
                <div className="flex items-center justify-center gap-2 px-4 py-2.5">
                  <Link2 className="w-3.5 h-3.5 text-[#1875FF]" />
                  <span className="text-sm font-medium text-[#1875FF]">{btnText}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-1 mt-1 px-1">
            <span className="text-xs text-gray-500">Delivered</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WhatsAppBubblePreview({
  title,
  content,
  placeholder,
  onUpdate,
  onGenerateAI,
  maxLength,
  defaultExpanded = false,
  buttonText = '',
  buttonUrl = '',
  onUpdateButton,
  approvalStatus = 'draft',
  pendingSubmission,
  onSubmitForApproval,
  submitting = false,
  hasApprovedVersion = false,
  readOnly = false,
}: WhatsAppBubblePreviewProps) {
  const tz = useTimezone();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(content);
  const [editButtonText, setEditButtonText] = useState(buttonText);
  const [editButtonUrl, setEditButtonUrl] = useState(buttonUrl);
  const [showButtonFields, setShowButtonFields] = useState(!!buttonText || !!buttonUrl);
  const [copied, setCopied] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [insertedVar, setInsertedVar] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef<number>(0);

  const TEMPLATE_VARIABLES = [
    { name: '{First_Name}', label: 'First Name' },
    { name: '{Last_Name}', label: 'Last Name' },
    { name: '{Company_Name}', label: 'Company Name' },
    { name: '{Company}', label: 'Company' },
    { name: '{Agent_Name}', label: 'Agent Name' },
    { name: '{Your_Company}', label: 'Your Company' },
    { name: '{Meeting_Date}', label: 'Meeting Date' },
    { name: '{Meeting_Time}', label: 'Meeting Time' },
    { name: '{Meeting_Link}', label: 'Meeting Link' },
  ];

  const trackCursor = () => {
    if (textareaRef.current) {
      cursorPosRef.current = textareaRef.current.selectionStart ?? 0;
    }
  };

  const handleInsertVariable = (varName: string) => {
    const pos = cursorPosRef.current;
    const before = editContent.slice(0, pos);
    const after = editContent.slice(pos);
    const newContent = before + varName + after;
    setEditContent(newContent);

    setInsertedVar(varName);
    setTimeout(() => setInsertedVar(null), 600);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const newPos = pos + varName.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
        cursorPosRef.current = newPos;
      }
    });
  };

  const isPendingReview = approvalStatus === 'in_review';
  const isRejected = approvalStatus === 'rejected';
  const canEdit = !isPendingReview && !readOnly;

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    if (!onSubmitForApproval) return;
    const finalButtonText = showButtonFields ? editButtonText : '';
    const finalButtonUrl = showButtonFields ? editButtonUrl : '';
    await onSubmitForApproval(editContent, finalButtonText, finalButtonUrl);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(content);
    setEditButtonText(buttonText);
    setEditButtonUrl(buttonUrl);
    setShowButtonFields(!!buttonText || !!buttonUrl);
    setIsEditing(false);
  };

  const handleAcceptGenerated = (generatedContent: string) => {
    setEditContent(generatedContent);
    onUpdate(generatedContent);
  };

  const handleRemoveButton = () => {
    setShowButtonFields(false);
    setEditButtonText('');
    setEditButtonUrl('');
  };

  const charCount = editContent.length;
  const isNearLimit = maxLength && charCount > maxLength * 0.9;
  const isOverLimit = maxLength && charCount > maxLength;


  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <div
        className="p-5 cursor-pointer"
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
            <MessageSquare className="w-5 h-5 text-[#1875FF]" />
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {isRejected && hasApprovedVersion ? (
              <div className="flex items-center gap-2">
                <StatusBadge status="active" />
                <span className="px-2.5 py-0.5 bg-red-50 text-red-700 text-xs font-semibold rounded-full flex items-center gap-1.5 border border-red-200">
                  <XCircle className="w-3 h-3" />
                  Change Rejected
                </span>
              </div>
            ) : (
              <StatusBadge status={approvalStatus} />
            )}
          </div>

          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <button
                onClick={() => {
                  setEditContent(content);
                  setEditButtonText(buttonText);
                  setEditButtonUrl(buttonUrl);
                  setShowButtonFields(!!buttonText || !!buttonUrl);
                  setIsEditing(true);
                  setIsExpanded(true);
                }}
                className="p-2 text-gray-400 hover:text-[#1875FF] hover:bg-[#1875FF]/10 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            {canEdit && onGenerateAI && (
              <AIRegenerateButton
                onRegenerate={onGenerateAI}
                onAccept={handleAcceptGenerated}
                currentContent={content}
              />
            )}
            <button
              onClick={handleCopy}
              disabled={!content}
              className="p-2 text-gray-400 hover:text-[#05E0D1] hover:bg-[#05E0D1]/10 rounded-lg transition-colors disabled:opacity-30"
              title="Copy"
            >
              {copied ? (
                <Check className="w-5 h-5 text-[#05E0D1]" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          {isEditing && canEdit ? (
            <div className="space-y-3 pt-4">
              <div>
                <textarea
                  ref={textareaRef}
                  value={editContent}
                  onChange={(e) => {
                    setEditContent(e.target.value);
                    trackCursor();
                  }}
                  onSelect={trackCursor}
                  onClick={trackCursor}
                  onKeyUp={trackCursor}
                  placeholder={placeholder}
                  rows={8}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm resize-none ${
                    isOverLimit ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {maxLength && (
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      <span
                        className={
                          isOverLimit
                            ? 'text-red-600 font-medium'
                            : isNearLimit
                            ? 'text-[#1875FF] font-medium'
                            : ''
                        }
                      >
                        {charCount}
                      </span>
                      {' / '}
                      {maxLength} characters
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowVariables(!showVariables)}
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-all duration-200 ${
                    showVariables
                      ? 'bg-[#05E0D1]/10 text-[#0bb5a5] border border-[#05E0D1]/30'
                      : 'text-gray-600 hover:text-[#0bb5a5] hover:bg-[#05E0D1]/5 border border-transparent'
                  }`}
                >
                  <Braces className="w-4 h-4" />
                  Add Variables
                </button>
                <div className="group relative">
                  <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-10">
                    Personalize your message by adding variables
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
                  </div>
                </div>
                {onUpdateButton && !showButtonFields && (
                  <button
                    type="button"
                    onClick={() => setShowButtonFields(true)}
                    className="flex items-center gap-2 text-sm text-[#1875FF] hover:text-[#1875FF]/80 font-medium px-3 py-2 rounded-lg hover:bg-[#1875FF]/5 transition-colors border border-transparent"
                  >
                    <Plus className="w-4 h-4" />
                    Add URL Button
                  </button>
                )}
              </div>

              {showVariables && (
                <div className="p-3 bg-gradient-to-br from-gray-50/80 to-[#05E0D1]/[0.03] border border-gray-200 rounded-xl">
                  <div className="flex flex-wrap gap-1.5">
                    {TEMPLATE_VARIABLES.map((v) => (
                      <button
                        key={v.name}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleInsertVariable(v.name)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer ${
                          insertedVar === v.name
                            ? 'bg-[#05E0D1]/15 text-[#0bb5a5] border border-[#05E0D1]/40 scale-[1.03]'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-[#05E0D1]/40 hover:bg-[#05E0D1]/[0.04] hover:text-[#0bb5a5] hover:shadow-sm'
                        }`}
                      >
                        <code className="font-mono text-[11px] tracking-tight">{v.name}</code>
                        {insertedVar === v.name && (
                          <Check className="w-3 h-3 text-[#05E0D1]" />
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">Click a variable to insert it at the cursor position</p>
                </div>
              )}

              {onUpdateButton && showButtonFields && (
                <div className="p-4 bg-gradient-to-br from-blue-50/80 to-cyan-50/40 border border-blue-100 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-[#1875FF]" />
                      <span className="text-sm font-semibold text-gray-800">URL Button</span>
                    </div>
                    <button
                      onClick={handleRemoveButton}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove button"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Button Text</label>
                      <input
                        type="text"
                        value={editButtonText}
                        onChange={(e) => setEditButtonText(e.target.value)}
                        placeholder="e.g. Learn More"
                        maxLength={25}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm bg-white"
                      />
                      <p className="text-xs text-gray-400 mt-1">{editButtonText.length}/25</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">URL</label>
                      <input
                        type="url"
                        value={editButtonUrl}
                        onChange={(e) => setEditButtonUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm bg-white"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Meta requires URLs to be sent as buttons in WhatsApp Business templates.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-gray-400">
                  Template changes require Meta approval before going live.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancel}
                    disabled={submitting}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isOverLimit || submitting || !editContent.trim()}
                    className="px-5 py-2 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg hover:shadow-lg hover:shadow-[#1875FF]/20 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm font-semibold flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Clock className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit for Approval
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="pt-4 space-y-4">
              {isRejected && !hasApprovedVersion && pendingSubmission?.rejection_reason && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Submission Rejected</p>
                    <p className="text-xs text-red-600 mt-0.5">{pendingSubmission.rejection_reason}</p>
                  </div>
                </div>
              )}

              {isPendingReview && pendingSubmission && !hasApprovedVersion && (
                <BubbleRenderer
                  text={pendingSubmission.submitted_text}
                  btnText={pendingSubmission.submitted_button_text}
                  btnUrl={pendingSubmission.submitted_button_url}
                />
              )}

              {isPendingReview && pendingSubmission && hasApprovedVersion && (
                <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                  <div className="rounded-xl border border-[#05E0D1]/30 bg-gradient-to-br from-[#05E0D1]/[0.03] to-transparent shadow-sm hover:shadow-md hover:shadow-[#05E0D1]/5 transition-all duration-300">
                    <div className="px-4 py-3 border-b border-[#05E0D1]/10">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#05E0D1]/10">
                          <ShieldCheck size={13} className="text-[#0bb5a5]" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">Currently Active</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#05E0D1]/10 text-[#0bb5a5] border border-[#05E0D1]/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#05E0D1]" />
                          Live
                        </span>
                      </div>
                    </div>
                    <div className="px-4 py-4">
                      <BubbleRenderer
                        text={content}
                        btnText={buttonText}
                        btnUrl={buttonUrl}
                      />
                    </div>
                  </div>
                  <div className="rounded-xl border border-dashed border-[#1875FF]/30 bg-gradient-to-br from-[#1875FF]/[0.03] to-transparent shadow-sm hover:shadow-md hover:shadow-[#1875FF]/5 transition-all duration-300">
                    <div className="px-4 py-3 border-b border-[#1875FF]/10">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#1875FF]/10">
                            <Clock size={13} className="text-[#1875FF]" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">Pending Approval</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#1875FF]/10 text-[#1875FF] border border-[#1875FF]/20 animate-pulse">
                            <Clock size={9} />
                            In Review
                          </span>
                        </div>
                        <span className="text-[11px] text-gray-400 font-medium hidden sm:block">
                          {toLocaleDateStringInTz(pendingSubmission.submitted_at, tz, undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    <div className="px-4 py-4">
                      <BubbleRenderer
                        text={pendingSubmission.submitted_text}
                        btnText={pendingSubmission.submitted_button_text}
                        btnUrl={pendingSubmission.submitted_button_url}
                      />
                    </div>
                  </div>
                </div>
              )}

              {isRejected && hasApprovedVersion && pendingSubmission && (
                <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                  <div className="rounded-xl border border-[#05E0D1]/30 bg-gradient-to-br from-[#05E0D1]/[0.03] to-transparent shadow-sm hover:shadow-md hover:shadow-[#05E0D1]/5 transition-all duration-300">
                    <div className="px-4 py-3 border-b border-[#05E0D1]/10">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#05E0D1]/10">
                          <ShieldCheck size={13} className="text-[#0bb5a5]" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">Currently Active</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#05E0D1]/10 text-[#0bb5a5] border border-[#05E0D1]/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#05E0D1]" />
                          Live
                        </span>
                      </div>
                    </div>
                    <div className="px-4 py-4">
                      <BubbleRenderer
                        text={content}
                        btnText={buttonText}
                        btnUrl={buttonUrl}
                      />
                    </div>
                  </div>
                  <div className="rounded-xl border border-dashed border-red-200 bg-gradient-to-br from-red-50/50 to-transparent shadow-sm hover:shadow-md hover:shadow-red-100/30 transition-all duration-300">
                    <div className="px-4 py-3 border-b border-red-100">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-50">
                            <XCircle size={13} className="text-red-500" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">Rejected Submission</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-600 border border-red-200">
                            <XCircle size={9} />
                            Rejected
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-4 space-y-3">
                      {pendingSubmission.rejection_reason && (
                        <div className="flex items-start gap-2.5 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-red-800">Reason</p>
                            <p className="text-xs text-red-600 mt-0.5">{pendingSubmission.rejection_reason}</p>
                          </div>
                        </div>
                      )}
                      <BubbleRenderer
                        text={pendingSubmission.submitted_text}
                        btnText={pendingSubmission.submitted_button_text}
                        btnUrl={pendingSubmission.submitted_button_url}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!isPendingReview && !(isRejected && hasApprovedVersion) && (
                <BubbleRenderer
                  text={content}
                  btnText={buttonText}
                  btnUrl={buttonUrl}
                />
              )}

              {!isPendingReview && !(isRejected && hasApprovedVersion) && !content && (
                <div className="flex justify-end">
                  <div className="max-w-sm bg-gray-100 rounded-2xl rounded-tr-sm px-4 py-3 text-sm text-gray-400 italic">
                    {placeholder || 'No message configured yet'}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}
